'use strict';

import React from 'react';
import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Tabs, Tab, Table, Button, Modal, Row, Col, Form, OverlayTrigger, Tooltip, Stack, Nav } from 'react-bootstrap';
import Select from 'react-select';

import { Formik } from 'formik';
import * as yup from 'yup';

import AppContext from '../AppContext';
import useConfirmation from '../helper/Confirmation';
import useRequest from '../helper/useRequest';
import { formatDate, formatTime, getDateFromString } from '../helper/DateTimeFormater';
import { toast } from 'react-toastify';
import Alert from '../helper/Alert';

import Loader from '../helper/Loader';
import TableHeader from '../helper/TableHeader';
import TimeslotEdit from './TimeslotEdit';


import AddContactperson from '../helper/Contactperson/AddContactpersonModal';
import EditContactperson from '../helper/Contactperson/EditContactpersonModal';
import VerifyContactperson from '../helper/Contactperson/VerifyContactpersonModal';

import Separator from '../helper/Separator';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
} from '@tanstack/react-table'
import PersonModal from '../helper/Modal/PersonModal';

import MandatoryFieldNotice from '../helper/MandatoryFieldNotice';

const columnHelper = createColumnHelper();

const aboSchema = yup.object().shape({
    Start: yup.string().required("Bitte ein Startdatum angeben."),
    End: yup.string().required("Bitte ein Enddatum eingeben."),
    Contingent: yup.string().required("Bitte ein Kontingent eingeben."),
    User: yup.number().required("Bitte die Anzahl an möglichen Benutzern angeben."),
    Contactperson: yup.number().required("Bitte die Anzahl an möglichen Kontaktpersonen angeben.")
});

function BusinessOperator() {
    const { request } = useRequest();
    const { getSalutation } = useContext(AppContext);

    const [id, setId] = useState(0);
    const [business, setBusiness] = useState(false);
    const [category, setCategory] = useState([]);
    const [contactPersons, setContactPersons] = useState([]);
    const [oldContactPerson, setOldContactPerson] = useState({});
    const [slots, setSlots] = useState([]);
    const [users, setUsers] = useState([]);
    const [abos, setAbos] = useState([]);
    const [slotsList, setSlotsList] = useState([]);
    const [additionalContingentList, setAdditionalContingentList] = useState([]);
    const [presets, setPresets] = useState([]);
    const [userCount, setUserCount] = useState(0);

    const [coupons, setCoupons] = useState({});

    const saveBusiness = async (values) => {
        try {
            let result = await request("/Operator/ChangeOrganisation", values);
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                toast.success("Das Unternehmen wurde erfolgreich gespeichert.");
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    }

    const updateAbo = (item) => {
        setAbos(items => {
            var foundItem = items.find(i => i.AboID == item.AboID);

            if (foundItem) {
                foundItem.Name = item.Name;
                foundItem.Price = item.Price;
                foundItem.Start = item.Start;
                foundItem.End = item.End;
                foundItem.Contingent = item.Contingent;
                foundItem.Radius = item.Radius;
                foundItem.User = item.User;
                foundItem.Contactperson = item.Contactperson;
            }
            else {
                items.push(item);
                setSlotsList(list => list.push([]));
            }

            return items;
        });
    }

    const deleteAbo = (item) => {
        setAbos(items => {
            return items.filter(i => i.AboID != item.AboID);
        })
    }

    const handleDeleteOrganisation = async (organisation) => {
        try {
            let result = await request("/Operator/DeleteOrganisation", { organisation });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                window.location.href = "/Operator/Businesses";
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                let path = location.pathname.split("/");
                if (path.length < 4) return;

                let id = path[3];
                setId(id);
                let result = await request("/Operator/GetBusinessWithAdditions", {id: id});
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten.");
                    }
                }
                else {
                    setBusiness(json.business);
                    setCategory(json.categories);
                    setContactPersons(json.contactPersons.map(item => {
                        item.Salutation = getSalutation(item.SalutationID);
                        return item;
                    }));
                    setOldContactPerson(json.oldContactPerson);
                    setSlots(json.slots);
                    setUsers(json.users.map(item => {
                        item.Salutation = getSalutation(item.SalutationID);
                        return item;
                    }));
                    setAbos(json.abos);
                    setSlotsList(json.slotsList);
                    setAdditionalContingentList(json.additionalContingentList);
                    setPresets(json.presets);
                    setUserCount(json.userCount);

                    setCoupons(json.coupon);
                }
            }
            catch (ex) {
                console.error(ex);
                toast.error("Es ist ein Fehler aufgetreten.");
            }
        };
        fetchData();
    }, []);

    if (business === false) return <Loader />

    return (
        <>
            <h1>Unternehmen ({business.OrganisationName})</h1>
            <Tabs
                defaultActiveKey="info"
                id="organisation-tabs"
                className="mb-3"
            >
                <Tab eventKey="info" title="Info">
                    <BusinessInfo organisation={business} category={category} contactPersons={contactPersons} oldContactPerson={oldContactPerson} save={saveBusiness} handleDeleteOrganisation={handleDeleteOrganisation} coupons={coupons} />
                </Tab>
                <Tab eventKey="termine" title="Termine">
                    <Timeslots slots={slots} setSlots={setSlots} />
                </Tab>
                <Tab eventKey="benutzer" title="Benutzer">
                    <Users users={users} setUsers={setUsers} id={id} />
                </Tab>
                <Tab eventKey="ansprechpartner" title="Ansprechpartner">
                    <Contactpersons persons={contactPersons} setPersons={setContactPersons} id={id}  />
                </Tab>
                <Tab eventKey="abonnements" title="Abonnements">
                    <Abonnements abos={abos} slotsList={slotsList} additionalContingentList={additionalContingentList} userCount={userCount} updateAbo={updateAbo} deleteAbo={deleteAbo} presets={presets} />
                </Tab>
            </Tabs>
        </>
    )
}

function BusinessInfo({ organisation, category, contactPersons, oldContactPerson, save, handleDeleteOrganisation, coupons }) {
    const formRef = useRef(null);
    const { Categories } = useContext(AppContext);

    const [contactPerson, setContactPerson] = useState([]);
    const [disabled, setDisabled] = useState(true);
    const confirmation = useConfirmation();

    const sortedCategories = useMemo(() => Categories.sort((a, b) => {
        const nameA = a.Description.toUpperCase();
        const nameB = b.Description.toUpperCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
    }).map(item => { return { value: item.CategoryID, label: item.Description } }), [Categories]);

    const abort = () => {
        setDisabled(true);

        formRef.current.setFieldValue("contactPerson", { value: oldContactPerson.ContactPersonID, label: oldContactPerson.FirstName + " " + oldContactPerson.LastName });
        formRef.current.setFieldValue("organisationName", organisation.OrganisationName);
        formRef.current.setFieldValue("street", organisation.Street);
        formRef.current.setFieldValue("houseNo", organisation.HouseNo);
        formRef.current.setFieldValue("additionToAddress", organisation.AdditionToAddress);
        formRef.current.setFieldValue("postCode", organisation.PostCode);
        formRef.current.setFieldValue("city", organisation.City);
        formRef.current.setFieldValue("court", organisation.Court);
        formRef.current.setFieldValue("compRegNo", organisation.CompRegNo);
        formRef.current.setFieldValue("chamberMembership", organisation.ChamberMembership);
        formRef.current.setFieldValue("isInstitution", organisation.IsInstitution);
        formRef.current.setFieldValue("filenameBusinessLicense", organisation.FilenameBusinessLicense);
        if (organisation.BillingStreet != null && organisation.BillingStreet.length > 0) {
            formRef.current.setFieldValue("isAlternativeBilling", true);
        } 
        formRef.current.setFieldValue("BillingEmail", organisation.BillingEmail);
        formRef.current.setFieldValue("BillingStreet", organisation.BillingStreet);
        formRef.current.setFieldValue("BillingHouseNo", organisation.BillingHouseNo);
        formRef.current.setFieldValue("BillingAdditionToAddress", organisation.BillingAdditionToAddress);
        formRef.current.setFieldValue("BillingPostCode", organisation.BillingPostCode);
        formRef.current.setFieldValue("BillingCity", organisation.BillingCity);

        let cats = category.map((item) => {
            var foundCategory = Categories.find(cat => cat.CategoryID == item);

            if (foundCategory) {
                return { value: foundCategory.CategoryID, label: foundCategory.Description };
            }

            return {};
        });
        formRef.current.setFieldValue("category", cats);
    };

    const handleSave = (values) => {
        setDisabled(true);
        save(values);
    };

    const confirmDelete = () => {
        confirmation.openModal("Organisation löschen", "Soll das Unternehmen " + organisation.OrganisationName + " und alle zugehörigen Daten wirklich unwiderruflich gelöscht werden?", () => { handleDeleteOrganisation(organisation) }, "Ja", "Nein")
    };

    useEffect(() => {
        if (!organisation) return;

        setContactPerson(contactPersons.map((val) => { return { value: val.ContactPersonID, label: val.FirstName + " " + val.LastName } }));
        formRef.current.setFieldValue("organisationName", organisation.OrganisationName);
        formRef.current.setFieldValue("street", organisation.Street);
        formRef.current.setFieldValue("houseNo", organisation.HouseNo);
        formRef.current.setFieldValue("additionToAddress", organisation.AdditionToAddress);
        formRef.current.setFieldValue("postCode", organisation.PostCode);
        formRef.current.setFieldValue("city", organisation.City);
        formRef.current.setFieldValue("court", organisation.Court);
        formRef.current.setFieldValue("compRegNo", organisation.CompRegNo);
        formRef.current.setFieldValue("chamberMembership", organisation.ChamberMembership);
        formRef.current.setFieldValue("isInstitution", organisation.IsInstitution);
        formRef.current.setFieldValue("filenameBusinessLicense", organisation.FilenameBusinessLicense);
        if (organisation.BillingStreet != null && organisation.BillingStreet.length > 0) {
            formRef.current.setFieldValue("isAlternativeBilling", true);
        } 
        formRef.current.setFieldValue("BillingEmail", organisation.BillingEmail);
        formRef.current.setFieldValue("BillingStreet", organisation.BillingStreet);
        formRef.current.setFieldValue("BillingHouseNo", organisation.BillingHouseNo);
        formRef.current.setFieldValue("BillingAdditionToAddress", organisation.BillingAdditionToAddress);
        formRef.current.setFieldValue("BillingPostCode", organisation.BillingPostCode);
        formRef.current.setFieldValue("BillingCity", organisation.BillingCity);

        let cats = category.map((item) => {
            var foundCategory = Categories.find(cat => cat.CategoryID == item);

            if (foundCategory) {
                return { value: foundCategory.CategoryID, label: foundCategory.Description };
            }

            return {};
        });
        formRef.current.setFieldValue("category", cats);

        if (oldContactPerson) {
            formRef.current.setFieldValue("contactPerson", { value: oldContactPerson.ContactPersonID, label: oldContactPerson.FirstName + " " + oldContactPerson.LastName });
        }
    }, [organisation]);

    return <>
        <Formik
        innerRef={(f) => formRef.current = f}
        validationSchema={yup.object().shape({})}
        onSubmit={(values) => {
            let organisationInfo = {
                OrganisationName: values.organisationName,
                Street: values.street,
                HouseNo: values.houseNo,
                AdditionToAddress: values.additionToAddress,
                PostCode: values.postCode,
                City: values.city,
                BillingEmail: values.BillingEmail,
                BillingStreet: values.BillingStreet,
                BillingHouseNo: values.BillingHouseNo,
                BillingAdditionToAddress: values.BillingAdditionToAddress,
                BillingPostCode: values.BillingPostCode,
                BillingCity: values.BillingCity,
                CompRegNo: values.compRegNo,
                Court: values.court,
                ChamberMembership: values.chamberMembership,
                FilenameBusinessLicense: values.filenameBusinessLicense,
                IsInstitution: values.isInstitution,
            };
            // category: values.category.map(item => item.value),
            let data = { organisationId: organisation.OrganisationID, categories: values.category.map(item => item.value), contactPerson: values.contactPerson.value, organisationInfo: organisationInfo };
            handleSave(data);
        }}
        initialValues={{
            organisationName: '',
            category: 'default',
            street: '',
            houseNo: '',
            additionToAddress: '',
            postCode: '',
            city: '',
            court: '',
            compRegNo: '',
            chamberMembership: '',
            filenameBusinessLicense: '',
            isInstitution: false,
            contactPerson: 0,
            BillingStreet: '',
            BillingHouseNo: '',
            BillingAdditionToAddress: '',
            BillingCity: '',
            BillingPostCode: '',
            BillingEmail: '',
            isAlternativeBilling: false,
        }}
    >
        {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            isValid,
            errors
        }) => {
                return <Form noValidate onSubmit={handleSubmit}>
                    <Row>
                        <Stack gap={2} className="col-md-6 mt-3">
                            <Row>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="organisationName">
                                        <Form.Label className="required">Name</Form.Label>
                                        <Form.Control isInvalid={errors.organisationName && touched.organisationName} placeholder="" name="organisationName" value={values.organisationName} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.organisationName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="category" onBlur={handleBlur} onChange={handleChange} name="category">
                                        <Form.Label className="required">Betriebskategorie</Form.Label>
                                        <Select isMulti isDisabled={disabled} name="category" value={values.category} options={sortedCategories} classname="basic-multi-select" placeholder="Bitte auswählen" onChange={
                                            (values) => {
                                                return formRef.current.setFieldValue("category", values);
                                            }
                                        } />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="8">
                                    <Form.Group controlId="street">
                                        <Form.Label className="required">Straße</Form.Label>
                                        <Form.Control isInvalid={errors.street && touched.street} placeholder="" name="street" value={values.street} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.street}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col sm="12" md="4">
                                    <Form.Group controlId="houseNo">
                                        <Form.Label className="required">Hausnummer</Form.Label>
                                        <Form.Control isInvalid={errors.houseNo && touched.houseNo} placeholder="" name="houseNo" value={values.houseNo} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.houseNo}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="additionToAddress">
                                        <Form.Label>Adresszusatz</Form.Label>
                                        <Form.Control isInvalid={errors.additionToAddress && touched.additionToAddress} placeholder="" name="additionToAddress" value={values.additionToAddress} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.additionToAddress}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="postCode">
                                        <Form.Label className="required">Postleitzahl</Form.Label>
                                        <Form.Control isInvalid={errors.postCode && touched.postCode} placeholder="" name="postCode" value={values.postCode} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.postCode}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="city">
                                        <Form.Label className="required">Ort</Form.Label>
                                        <Form.Control isInvalid={errors.city && touched.city} placeholder="" name="city" value={values.city} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.city}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="court">
                                        <Form.Label className="required">Amtsgericht</Form.Label>
                                        <Form.Control isInvalid={errors.court && touched.court} placeholder="" name="court" value={values.court} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.court}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col sm="12" md="6">
                                    <Form.Group controlId="compRegNo">
                                        <Form.Label className="required">Handelsregisternummer</Form.Label>
                                        <Form.Control isInvalid={errors.compRegNo && touched.compRegNo} placeholder="" name="compRegNo" value={values.compRegNo} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.compRegNo}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group controlId="chamberMembership">
                                <Form.Label className="required">Kammerzugehörigkeit</Form.Label>
                                <Form.Control isInvalid={errors.chamberMembership && touched.chamberMembership} placeholder="" name="chamberMembership" value={values.chamberMembership} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                <Form.Control.Feedback type="invalid">
                                    {errors.chamberMembership}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="filenameBusinessLicense">
                                <Form.Label className="required">Gewerbeschein-Upload</Form.Label>
                                <Form.Control isInvalid={errors.filenameBusinessLicense && touched.filenameBusinessLicense} placeholder="" name="filenameBusinessLicense" value={values.filenameBusinessLicense} onBlur={handleBlur} onChange={handleChange} disabled />
                                <Form.Control.Feedback type="invalid">
                                    {errors.filenameBusinessLicense}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="isInstitution">
                                <Form.Label>Institution des öffentlichen Rechts</Form.Label>
                                <Form.Check type="checkbox" label="ist Anstalt oder Körperschaft des öffentlichen Rechts" checked={values.isInstitution} onChange={handleChange} disabled={disabled} />
                            </Form.Group>

                            <Form.Group as={Row} controlId="contactPerson">
                                <Col><Form.Label>Ansprechpartner</Form.Label></Col>
                                <Select name="contactPerson" options={contactPerson} value={values.contactPerson} placeholder="Bitte ausw&auml;hlen" onChange={
                                    (values) => {
                                        formRef.current.setFieldValue("contactPerson", values);
                                    }
                                } isDisabled={disabled} />
                            </Form.Group>

                            <Separator text="" />

                            <Form.Group className="mb-3" controlId="isAlternativeBilling">
                                <Form.Check type="checkbox" label="Abweichende Rechnungsadresse" checked={values.isAlternativeBilling} onChange={handleChange} disabled={disabled} />
                            </Form.Group>

                            {values.isAlternativeBilling && (
                                <>
                                    <Form.Group controlId="BillingEmail">
                                        <Form.Label className="required">Rechnungsadresse: E-Mail-Adresse</Form.Label>
                                        <Form.Control isInvalid={errors.BillingEmail && touched.BillingEmail} placeholder="" name="BillingEmail" value={values.BillingEmail} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.BillingEmail}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Row>
                                        <Col sm="12" md="6">
                                            <Form.Group controlId="BillingStreet">
                                                <Form.Label className="required">Rechnungsadresse: Straße</Form.Label>
                                                <Form.Control isInvalid={errors.BillingStreet && touched.BillingStreet} placeholder="" name="BillingStreet" value={values.BillingStreet} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.BillingStreet}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col sm="12" md="6">
                                            <Form.Group controlId="BillingHouseNo">
                                                <Form.Label className="required">Rechnungsadresse: Hausnummer</Form.Label>
                                                <Form.Control isInvalid={errors.BillingHouseNo && touched.BillingHouseNo} placeholder="" name="BillingHouseNo" value={values.BillingHouseNo} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.BillingHouseNo}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12" md="6">
                                            <Form.Group controlId="BillingAdditionToAddress">
                                                <Form.Label>Rechnungsaddresse: Adresszusatz</Form.Label>
                                                <Form.Control isInvalid={errors.BillingAdditionToAddress && touched.BillingAdditionToAddress} placeholder="" name="BillingAdditionToAddress" value={values.BillingAdditionToAddress} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.BillingAdditionToAddress}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12" md="6">
                                            <Form.Group controlId="BillingPostCode">
                                                <Form.Label className="required">Rechnungsadresse: Postleitzahl</Form.Label>
                                                <Form.Control isInvalid={errors.BillingPostCode && touched.BillingPostCode} placeholder="" name="BillingPostCode" value={values.BillingPostCode} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.BillingPostCode}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col sm="12" md="6">
                                            <Form.Group controlId="BillingCity">
                                                <Form.Label className="required">Rechnungsadresse: Stadt</Form.Label>
                                                <Form.Control isInvalid={errors.BillingCity && touched.BillingCity} placeholder="" name="BillingCity" value={values.BillingCity} onBlur={handleBlur} onChange={handleChange} disabled={disabled} />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.BillingCity}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <Row>
                                <Col sm="6">
                                    {disabled && <Button variant="primary" type="button" onClick={() => setDisabled(!disabled)}><i className="fa-solid fa-pen me-2"></i>Bearbeiten</Button>}
                                    {!disabled && (
                                        <>
                                            <Button variant="secondary" type="button" className="me-2" onClick={() => abort()}><i className="fa-solid fa-times me-2"></i>Abbrechen</Button>
                                            <Button variant="primary" type="submit"><i className="fa-solid fa-check me-2"></i>Speichern</Button>
                                        </>
                                    )}
                                </Col>
                                <Col sm="6" className="text-end">
                                    {disabled && <Button variant="danger" type="button" onClick={() => confirmDelete()}><i className="fa-solid fa-trash me-2"></i>Löschen</Button>}
                                </Col>
                            </Row>
                        </Stack>

                        <Stack gap={2} className="col-md-6 mt-3">
                            <Form.Label>Coupons</Form.Label>
                            <CouponTable coupons={coupons} />
                        </Stack>
                    </Row>
            </Form>
        }}
    </Formik>
    
    </>;
}

function Timeslots({ slots, setSlots }) {
    const { request } = useRequest();
    const { getSalutation } = useContext(AppContext);
    const confirmation = useConfirmation();
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [modal, setModal] = useState(false);
    const [timeslots, setTimeslots] = useState([]);
    const [timeslot, setTimeslot] = useState(false);

    const now = new Date();

    const cancelTimeslot = async (id) => {
        try {
            let result = await request("/Operator/CancelTimeslot", { timeslotId: id });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                toast.success("Zeitsatz storniert.");
                updateTimeslots(id);
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    const deleteTimeslot = async (id) => {
        try {
            let result = await request("/Operator/DeleteTimeslot", { timeslotId: id });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                toast.success("Zeitsatz gelöscht.");
                updateTimeslots(id);
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    const updateTimeslots = (id) => {
        setSlots((items) => {
            var newItems = items.filter((timeslot) => {
                if (timeslot.TimeslotID == id) {
                    return false;
                }
                return true;
            });
            return newItems;
        });
    }

    const openEdit = (timeslot) => {
        setTimeslot(slots.find(m => m.TimeslotID == timeslot.TimeslotID));
        setModal(true);
    }

    const columns = [
        columnHelper.accessor('Appointment', {
            header: () => <span>Datum</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('TimeStart', {
            header: () => <span>Startzeit</span>,
            cell: info => formatTime(info.getValue()),
        }),
        columnHelper.accessor('TimeEnd', {
            header: () => <span>Endzeit</span>,
            cell: info => formatTime(info.getValue()),
        }),
        columnHelper.accessor('ContactSchoolDescription', {
            header: () => <span>Schule</span>,
            cell: info => getContact(info.row.original),
            enableSorting: false,
        }),
        columnHelper.accessor('Grade', {
            header: () => <span>Stufe</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Class', {
            header: () => <span>Klasse</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Room', {
            header: () => <span>Raum</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Equipment', {
            header: () => <span>Ausstattung</span>,
            cell: info => info.getValue(),
            enableSorting: false,
        }),
        columnHelper.accessor('TimeslotID', {
            size: 130,
            header: () => <span></span>,
            cell: info => {
                let d = getDateFromString(info.row.original.Appointment, info.row.original.TimeStart);
                if (d < now) return;

                if (info.row.original.BookedBusiness == 0) {
                    return (
                        <div className="text-end">
                            <OverlayTrigger overlay={<Tooltip>Bearbeiten</Tooltip>}>
                                <Button variant="primary" onClick={() => { openEdit(info.row.original) }}><i className="fa-solid fa-fw fa-pen"></i></Button>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip>Löschen</Tooltip>}>
                                <Button variant="danger" onClick={() => confirmation.openModal("Wirklich löschen?", "Möchten Sie diesen Termin wirklich löschen?", () => { deleteTimeslot(info.getValue()) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-trash"></i></Button>
                            </OverlayTrigger>
                        </div>
                    );
                }
                else {
                    return (
                        <div className="text-end">
                            <OverlayTrigger overlay={<Tooltip>Bearbeiten</Tooltip>}>
                                <Button variant="primary" onClick={() => { openEdit(info.row.original) }}><i className="fa-solid fa-fw fa-pen"></i></Button>
                            </OverlayTrigger>

                            <OverlayTrigger overlay={<Tooltip>Stornieren</Tooltip>}>
                                <Button variant="warning" onClick={() => confirmation.openModal("Wirklich stornieren?", "Möchten Sie diesen Termin wirklich stornieren?", () => { cancelTimeslot(info.getValue()) }, "Ja", "Nein")}><i class="fa-solid fa-fw fa-times"></i></Button>
                            </OverlayTrigger>

                            <OverlayTrigger overlay={<Tooltip>Löschen</Tooltip>}>
                                <Button variant="danger" onClick={() => confirmation.openModal("Wirklich löschen?", "Möchten Sie diesen Termin wirklich löschen?", () => { deleteTimeslot(info.getValue()) }, "Ja", "Nein")}><i className="fa-solid fa-fw fa-trash"></i></Button>
                            </OverlayTrigger>
                        </div>
                    );
                }  
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: timeslots || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const getContact = (slot) => {
        return (
            <>
                <div>{slot.OrganisationName}, {slot.Street} {slot.HouseNo}, {slot.PostCode} {slot.City}</div>
                {slot.ContactSchool && <div>{getSalutation(slot.ContactSchool.SalutationID)} {slot.ContactSchool.Title} {slot.ContactSchool.FirstName} {slot.ContactSchool.LastName} ({slot.ContactSchool.Email})</div>}
            </>
        );
    }

    const editTimeslot = async (timeslot) => {
        setSlots((items) => {
            return items.map((item) => {
                if (item.TimeslotID == timeslot.TimeslotID) {
                    item.Appointment = timeslot.Appointment;
                    item.TimeStart = timeslot.TimeStart;
                    item.TimeEnd = timeslot.TimeEnd;
                    item.Grade = timeslot.Grade;
                    item.Class = timeslot.Class;
                    item.Room = timeslot.Room;
                    item.Equipment = timeslot.Equipment;
                    item.Category = timeslot.Category;
                    item.ExpireTime = timeslot.ExpireTime;
                    item.ContactPersonSchool = timeslot.ContactPersonSchool;
                    item.ContactSchool = timeslot.ContactSchool;
                    item.ContactPersonOrganisation = timeslot.ContactPersonOrganisation;
                    item.ContactOrganisation = timeslot.ContactOrganisation;
                }

                return item;
            })
        });
    }

    useEffect(() => {
        const slotData = slots.map(oldItem => {
            const item = { ...oldItem };
            item.Appointment = formatDate(item.Appointment);
            item.ContactSchoolDescription = oldItem.OrganisationName + ", " + oldItem.Street + " " + oldItem.HouseNo + ", " + oldItem.PostCode + " " + oldItem.City;
            if (oldItem.ContactSchool) {
                item.ContactSchoolDescription += getSalutation(oldItem.ContactSchool.SalutationID) + " " + oldItem.ContactSchool.Title + " " + oldItem.ContactSchool.FirstName + " " + oldItem.ContactSchool.LastName + "(" + oldItem.ContactSchool.Email + ")";
            }
            item.Equipment = item.Equipment.map((item) => { return item.Description; }).join("; ");

            return item;
        });

        setTimeslots(slotData);
    }, [slots]);

    return (
        <>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <TimeslotEdit timeslot={timeslot} open={modal} setOpen={setModal} editTimeslot={editTimeslot} />
        </>
    );
}

function Users({ users, setUsers, id }) {
    const { request } = useRequest();

    const { getSalutation } = useContext(AppContext);
    const confirmation = useConfirmation();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [modal, setModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [person, setPerson] = useState(false);
    const [message, setMessage] = useState(false);
    const [editMessage, setEditMessage] = useState(false);

    const confirmDeletePerson = (person) => {
        if (users.length == 1) {
            var text = <>
                <p>Durch die Löschung des letzten Benutzers, werden alle gebuchten Termine automatisch storniert.</p>
                <p>Soll der Benutzer {person.FirstName} {person.LastName} wirklich unwiderruflich gelöscht werden?</p>
            </>;
            confirmation.openModal("Benutzer löschen", text, () => { handleDeletePerson(person) }, "Ja", "Nein")
        }
        else {
            confirmation.openModal("Benutzer löschen", "Soll der Benutzer " + person.FirstName + " " + person.LastName + " wirklich unwiderruflich gelöscht werden?", () => { handleDeletePerson(person) }, "Ja", "Nein")
        }
    };

    const handleDeletePerson = async (person) => {
        try {
            let result = await request("/Operator/DeleteUser", { UserID: person.UserID, OrganisationID: id });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                setUsers((persons) => {
                    return persons.filter(p => p.UserID != person.UserID);
                });
                toast.success("Benutzer erfolgreich gelöscht.");
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };

    const handleSubmit = async (values, editMode) => {
        if (editMode) {
            editUser(values);
        }
        else {
            addUser(values);
        }
    };

    const addUser = async (values) => {
        try {
            setMessage("");
            const result = await request("/Operator/AddUser", { OrganisationID: id, SalutationID: values.salutation, Title: values.title, FirstName: values.firstname, LastName: values.lastname, Email: values.email, PhoneNo: values.phone, Username: values.username, IsAdmin: values.admin });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Benutzer erfolgreich hinzugefügt.");
                updatePersons(json.user);
                setModal(false);
            }
            else {
                setMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const editUser = async (values) => {
        try {
            setEditMessage("");
            const result = await request("/Operator/EditUser", { UserID: person.UserID, OrganisationID: id, SalutationID: values.salutation, Title: values.title, FirstName: values.firstname, LastName: values.lastname, Email: values.email, PhoneNo: values.phone, Username: values.username, IsAdmin: values.admin });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Benutzer erfolgreich bearbeitet.");
                updatePersons(json.user);
                setPerson(false);
                setEditModal(false);
            }
            else {
                setEditMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setEditMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const updatePersons = (person) => {
        setUsers(oldUsers => {
            const temp = [...oldUsers];
            const foundUser = temp.find(u => u.UserID == person.UserID);

            if (foundUser) {
                foundUser.Username = person.Username;
                foundUser.SalutationID = person.SalutationID;
                foundUser.Salutation = getSalutation(person.SalutationID);
                foundUser.Title = person.Title;
                foundUser.FirstName = person.FirstName;
                foundUser.LastName = person.LastName;
                foundUser.Email = person.Email;
                foundUser.PhoneNo = person.PhoneNo;
                foundUser.IsAdmin = person.IsAdmin;
            }
            else {
                person.Salutation = getSalutation(person.SalutationID);
                temp.push(person);
            }

            return temp;
        });
    };

    const columns = [
        columnHelper.accessor('Username', {
            header: () => <span>Benutzername</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Salutation', {
            header: () => <span>Anrede</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Title', {
            header: () => <span>Titel</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('FirstName', {
            header: () => <span>Vorname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('LastName', {
            header: () => <span>Nachname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Email', {
            header: () => <span>E-Mail</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('PhoneNo', {
            header: () => <span>Telefon</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('OrganisationID', {
            size: 195,
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">
                    <Button type="button" variant="primary" size="sm" onClick={(e) => { setEditModal(true); setPerson(info.row.original); }} className="ms-2">Bearbeiten</Button>
                    <Button type="button" variant="danger" size="sm" onClick={(e) => confirmDeletePerson(info.row.original)} className="ms-2">Löschen</Button>
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: users || [],
        columns,
        state: {
            sorting,
            columnFilters
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <div className="text-end">
                <Button type="button" variant="primary" onClick={(e) => setModal(true)}>Benutzer hinzufügen</Button>
            </div>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <PersonModal modal={modal} closeModal={() => { setModal(false); setPerson(false); }} onSubmit={handleSubmit} message={message} editMode={false} />
            <PersonModal modal={editModal} closeModal={() => { setEditModal(false); setPerson(false); }} person={person} onSubmit={handleSubmit} message={editMessage} editMode={true} />
        </>
    );
}

function Contactpersons({ persons, setPersons, id }) {
    const { request } = useRequest();
    const { getSalutation } = useContext(AppContext);
    const confirmation = useConfirmation();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [addModal, setAddModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [verifyModal, setVerifyModal] = useState(false);
    const [person, setPerson] = useState(false);
    const [addMessage, setAddMessage] = useState('');
    const [editMessage, setEditMessage] = useState('');

    const confirmDeletePerson = (person) => {
        if (person.DefaultPerson) {
            //return toast.error("Die Standard-Kontaktperson kann nicht gelöscht werden.");
        }
        confirmation.openModal("Kontaktperson löschen?", "Soll die Kontaktperson " + person.FirstName + " " + person.LastName + " wirklich unwiderruflich gelöscht werden?", () => { deletePerson(person) }, "Ja", "Nein")
    }

    const addPerson = async (values) => {
        try {
            setAddMessage("");
            const result = await request("/Operator/AddContactperson", {
                OrganisationID: id,
                SalutationID: values.salutation,
                Title: values.title,
                FirstName: values.firstname,
                LastName: values.lastname,
                Email: values.email,
                PhoneNo: values.phone,
                DefaultPerson: values.default,
                ReminderOptIn: values.reminderOptIn,
                CriminalRecord: values.criminalRecord == true ? '2' : '0'
            });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Referent erfolgreich hinzugefügt.");
                updatePersons(json.person);
                setAddModal(false);
            }
            else {
                setAddMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setAddMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const editPerson = async (values) => {
        try {
            setEditMessage("");
            const result = await request("/Operator/EditContactperson", {
                OrganisationID: id,
                SalutationID: values.salutation,
                Title: values.title,
                FirstName: values.firstname,
                LastName: values.lastname,
                Email: values.email,
                PhoneNo: values.phone,
                DefaultPerson: values.default,
                ContactPersonID: person.ContactPersonID,
                ReminderOptIn: values.reminderOptIn,
                CriminalRecord: values.criminalRecord == true ? '2' : '0'
            });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Referent erfolgreich gespeichert.");
                updatePersons(json.person);
                setEditModal(false);
                setPerson(false);
            }
            else {
                setEditMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setEditMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const deletePerson = async (person) => {
        try {
            let result = await request("/Operator/DeleteContactperson", { person });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                if (person.DefaultPerson) {
                    setPersons((persons) => {
                        return persons.map(p => {
                            if (p.ContactPersonID == person.ContactPersonID) {
                                p.Title = "";
                                p.FirstName = "";
                                p.LastName = "";
                                p.Email = "";
                                p.PhoneNo = "";
                            }
                            return p;
                        });
                    });
                }
                else {
                    setPersons((persons) => {
                        return persons.filter(p => p.ContactPersonID != person.ContactPersonID);
                    });
                }
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    }

    const updatePersons = (person) => {
        setPersons((items) => {
            if (person.DefaultPerson) {
                for (var i = 0; i < items.length; i++) {
                    items[i].DefaultPerson = false;
                }
            }

            person.Salutation = getSalutation(person.SalutationID);

            var i = items.findIndex(item => item.ContactPersonID == person.ContactPersonID);
            if (i > -1) {
                items[i] = person;
            } else {
                items.push(person);
            }

            return [...items];
        });
    }

    const verifyPerson = async (values) => {
        try {
            setEditMessage("");
            const result = await request("/Operator/EditContactperson", {
                OrganisationID: id,
                SalutationID: person.SalutationID,
                Title: person.Title,
                FirstName: person.FirstName,
                LastName: person.LastName,
                Email: person.Email,
                PhoneNo: person.PhoneNo,
                DefaultPerson: person.DefaultPerson,
                ContactPersonID: person.ContactPersonID,
                ReminderOptIn: person.ReminderOptIn,
                CriminalRecord: 2
            });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Referent erfolgreich verifiziert.");
                updatePersons(json.person);
                setVerifyModal(false);
                setPerson(false);
            }
            else {
                setEditMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setEditMessage("Es ist ein Fehler aufgetreten.");
        }
    }

    const rejectPerson = async (values) => {
        try {
            setEditMessage("");
            const result = await request("/Operator/EditContactperson", {
                OrganisationID: id,
                SalutationID: person.SalutationID,
                Title: person.Title,
                FirstName: person.FirstName,
                LastName: person.LastName,
                Email: person.Email,
                PhoneNo: person.PhoneNo,
                DefaultPerson: person.DefaultPerson,
                ContactPersonID: person.ContactPersonID,
                ReminderOptIn: person.ReminderOptIn,
                CriminalRecord: 0
            });
            const json = result.data;

            if (json.r == 1) {
                toast.success("Referent abgelehnt.");
                updatePersons(json.person);
                setVerifyModal(false);
                setPerson(false);
            }
            else {
                setEditMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setEditMessage("Es ist ein Fehler aufgetreten.");
        }
    };

    const columns = [
        columnHelper.accessor('Salutation', {
            header: () => <span>Anrede</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Title', {
            header: () => <span>Titel</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('FirstName', {
            header: () => <span>Vorname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('LastName', {
            header: () => <span>Nachname</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('Email', {
            header: () => <span>E-Mail</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('PhoneNo', {
            header: () => <span>Telefon</span>,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('DefaultPerson', {
            header: () => <span>Standard</span>,
            cell: info => info.getValue() ? <div className="text-center"><i className="fas fa-lg fa-check"></i></div> : '',
            enableColumnFilter: false,
        }),
        //columnHelper.accessor('CriminalRecord', {
        //    header: () => <span>Erweitertes Führungszeugnis</span>,
        //    cell: info => info.getValue() == 0 ? 'Kein Führungszeugnis' : info.getValue() == 1 ? 'Nicht verifiziertes Führungszeugnis' : 'Verifiziertes Führungszeugnis',
        //    enableColumnFilter: false,
        //}),
        columnHelper.accessor('OrganisationID', {
            size: 195,
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">            
                    {/*{(info.row.original.CriminalRecord == 1) && ( <Button type="button" variant="success" size="sm" onClick={(e) => { setPerson(info.row.original); setVerifyModal(true); }} className="ms-2">Verifizieren</Button>)}*/}
                    <Button type="button" variant="primary" size="sm" onClick={(e) => { setPerson(info.row.original); setEditModal(true); }} className="ms-2">Bearbeiten</Button>
                    <Button type="button" variant="danger" size="sm" onClick={(e) => confirmDeletePerson(info.row.original)} className="ms-2">Löschen</Button>
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const reactTable = useReactTable({
        data: persons || [],
        columns,
        state: {
            sorting,
            columnFilters
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <div className="text-end">
                <Button type="button" variant="primary" onClick={(e) => setAddModal(true) }>Ansprechpartner hinzufügen</Button>
            </div>
            <Table striped>
                <thead>
                    {reactTable.getHeaderGroups().map(headerGroup => (
                        <TableHeader key={headerGroup.id} table={reactTable} headerGroup={headerGroup} flexRender={flexRender} />
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
            <AddContactperson modal={addModal} setModal={setAddModal} addPerson={addPerson} contactLabel="Referent" message={addMessage} userType={"Operator"} />
            <EditContactperson modal={editModal} setModal={setEditModal} editPerson={editPerson} contactLabel="Referent" person={person} setPerson={setPerson} message={editMessage} userType={"Operator"} />
            <VerifyContactperson modal={verifyModal} setModal={setVerifyModal} person={person} verifyPerson={verifyPerson} rejectPerson={rejectPerson} contactLabel="Referent" message={addMessage} />
        </>
    );
}

function Abonnements({ abos, slotsList, additionalContingentList, userCount, updateAbo, deleteAbo, presets }) {
    const confirmation = useConfirmation();
    const { request } = useRequest();

    const [key, setKey] = useState(0);
    const [addAboModal, setAddAboModal] = useState(false);
    const [editAboModal, setEditAboModal] = useState(false);
    const [editItem, setEditItem] = useState(false);
    
    
    
    const confirmDeleteAbo = (item) => {
        confirmation.openModal("Paket löschen?", "Soll das Paket wirklich unwiderruflich gelöscht werden?", () => { handleDeleteAbo(item) }, "Ja", "Nein")
    }

    const handleDeleteAbo = async (item) => {
        try {
            const result = await request("/Operator/DeleteAbo", item);
            const json = result.data;

            if (json.r == 1) {
                toast.success("Paket erfolgreich gelöscht.");
                deleteAbo(item);
            }
            else {
                toast.error(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    }

    const editAbo = (item) => {
        setEditAboModal(true);
        setEditItem(item);
    }

    return (
        <>
            <Tab.Container id="abonnement-tabs" className="mb-3" defaultActiveKey={key}>
                <Row>
                    <Col sm={3} className="position-relative">
                        <h3>Gebuchte Pakete <Button id="addAbo" type="button" variant="primary" onClick={(e) => setAddAboModal(true)} className="ms-2"><i className="fa-solid fa-plus"></i></Button></h3>
                        <Nav variant="pills" className="flex-column">
                            {abos.map((item, index) => {
                                return <Nav.Item key={index}>
                                    <Nav.Link eventKey={index}>{formatDate(item.Start)} - {formatDate(item.End)} ({slotsList[index] ? slotsList[index].length : 0} / {item.Contingent + additionalContingentList[index]})</Nav.Link>
                                </Nav.Item>
                            })}
                        </Nav>
                    </Col>
                    <Col sm={9}>
                        <Tab.Content>
                            {abos.map((item, index) => {
                                return (
                                    <Tab.Pane key={index} eventKey={index}>
                                        <Row className="align-items-center">
                                            <Col sm={8}>
                                                <span className="contingent me-2">Vorhandenes Kontingent: {slotsList[index] ? slotsList[index].length : 0} / {item.Contingent + additionalContingentList[index]}</span>
                                                <span className="user">Anzahl Benutzer: {userCount} / {item.User}</span>
                                            </Col>
                                            <Col sm={4} className="text-end">
                                                <Button variant="primary" size="sm" type="button" className="me-1" onClick={(e) => { setEditAboModal(true); setEditItem({ ...item }); }}>Bearbeiten</Button>
                                                <Button variant="danger" size="sm" type="button" onClick={(e) => { confirmDeleteAbo(item) } }>Löschen</Button>
                                            </Col>
                                        </Row>
                                        
                                        <div className="timeslots">
                                            <Table striped>
                                                <thead>
                                                    <tr>
                                                        <th>Datum</th>
                                                        <th>Startzeit</th>
                                                        <th>Endzeit</th>
                                                        <th>Schule</th>
                                                        <th>Stufe</th>
                                                        <th>Klasse</th>
                                                        <th>Ausstattung</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {slotsList[index] && slotsList[index].map((ts) => {
                                                        return <Tablerow key={ts.TimeslotID} timeslot={ts} />
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Tab.Pane>
                                );
                            })}
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>

            <AddAboModal modal={addAboModal} setModal={setAddAboModal} updateAbo={updateAbo} presets={presets} />
            <EditAboModal modal={editAboModal} setModal={setEditAboModal} updateAbo={updateAbo} item={editItem} />
        </>
    );
}

const Tablerow = (props) => {
    if (!props) return null;

    return <tr>
        <td>{formatDate(props.timeslot.Appointment)}</td>
        <td>{formatTime(props.timeslot.TimeStart)}</td>
        <td>{formatTime(props.timeslot.TimeEnd)}</td>
        <td>{props.timeslot.OrganisationName + ", " + props.timeslot.Street + " " + props.timeslot.HouseNo + ", " + props.timeslot.PostCode + " " + props.timeslot.City}</td>
        <td>{props.timeslot.Grade}</td>
        <td>{props.timeslot.Class}</td>
        <td>{props.timeslot.Equipment.map((item) => { return item.Description; }).join("; ")}</td>
        <td>{props.timeslot.StatusString}</td>
    </tr>
}

const AddAboModal = ({ modal, setModal, updateAbo, presets }) => {
    const { request } = useRequest();
    const [period, setPeriod] = useState(0);
    const closeModal = () => {
        setModal(false);
    }
    const [message, setMessage] = useState("");
    const formRef = useRef();

    const addAbo = async (values) => {
        try {
            let path = location.pathname.split("/");
            if (path.length < 4) return;

            let id = path[3];
            if (period != 0) {
                values.Price = values.Price * period;
            }

            let data = { ...values, OrganisationID: id };
            setMessage("");

            const result = await request("/Operator/AddAbo", data);
            const json = result.data;

            if (json.r == 1) {
                toast.success("Paket erfolgreich hinzugefügt.");
                updateAbo(json.abo);
                closeModal();
            }
            else {
                setMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setMessage("Es ist ein Fehler aufgetreten.");
        }
    }

    const changePreset = async (e) => {
        if (e.target.value == 0) return;

        let foundPreset = presets.find(item => item.AboID == e.target.value);

        if (!foundPreset) return;

        formRef.current.setFieldValue("Name", foundPreset.Name);
        formRef.current.setFieldValue("Price", foundPreset.Price);
        formRef.current.setFieldValue("Contingent", foundPreset.Contingent);
        formRef.current.setFieldValue("Radius", foundPreset.Radius);
        formRef.current.setFieldValue("User", foundPreset.User);
        formRef.current.setFieldValue("Contactperson", foundPreset.Contactperson);
        formRef.current.setFieldValue("End", new Date(today.getFullYear(), today.getMonth() + foundPreset.Period, 0, 23, 59, 59).toISOString().slice(0, 10));
        setPeriod(foundPreset.Period);
    }

    let today = new Date();
    let firstDay = new Date(today.getFullYear(), today.getMonth(), 1, 23, 59, 59);
    let lastDay = new Date(today.getFullYear(), today.getMonth()+1, 0, 23, 59, 59);

    return (
        <Modal show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Paket hinzufügen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert type="error" message={message} />}

                <Formik
                    innerRef={(f) => (formRef.current = f)}
                    validationSchema={aboSchema}
                    onSubmit={(values) => {
                        addAbo(values);
                    }}
                    initialValues={{
                        Start: firstDay.toISOString().split("T")[0],
                        End: lastDay.toISOString().split("T")[0],
                        Contingent: 5,
                        Radius: 25,
                        User: 5,
                        Contactperson: 5
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        touched,
                        isValid,
                        errors,
                    }) => {
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group as={Row} className="mb-3" controlId="Preset">
                                    <Form.Label column sm={6}>Presets</Form.Label>
                                    <Col sm={6}>
                                        <Form.Select isInvalid={errors.Preset && touched.Preset} name="Preset" onBlur={handleBlur} onChange={changePreset} >
                                            <option value="0">Preset wählen</option>
                                            {presets.map(item => {
                                                return <option value={item.AboID}>{item.Name}</option>
                                            })}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Start}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Name">
                                    <Form.Label className="required" column sm={6}>Name</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Name && touched.Name} type="text" name="Name" value={values.Name} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Name}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Price">
                                    <Form.Label className="required" column sm={6}>Preis pro Monat wenn Preset ausgewählt sonst Gesamtpreis (exkl. MwSt)</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Price && touched.Price} type="number" step="0.01" min="0" name="Price" value={values.Price} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Price}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Start">
                                    <Form.Label className="required" column sm={6}>Startdatum</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Start && touched.Start} type="date" name="Start" value={values.Start} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Start}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="End">
                                    <Form.Label className="required" column sm={6}>Enddatum</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.End && touched.End} type="date" name="End" value={values.End} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.End}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Contingent">
                                    <Form.Label className="required" column sm={6}>Kontingent</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Contingent && touched.Contingent} type="number" min="0" name="Contingent" value={values.Contingent} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Contingent}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Radius">
                                    <Form.Label className="required" column sm={6}>Radius</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Radius && touched.Radius} type="number" min="0" name="Radius" value={values.Radius} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Radius}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="User">
                                    <Form.Label className="required" column sm={6}>Anzahl Benutzer</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.User && touched.User} type="number" min="0" name="User" value={values.User} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.User}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Contactperson">
                                    <Form.Label className="required" column sm={6}>Anzahl Ansprechpartner</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Contactperson && touched.Contactperson} type="number" min="0" name="Contactperson" value={values.Contactperson} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Contactperson}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                            </Form>
                        )
                    }}
                </Formik>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Abbrechen
                </Button>
                <Button variant="primary" type="button" onClick={(e) => formRef.current.handleSubmit() }>
                    Hinzufügen
                </Button>
            </Modal.Footer>
        </Modal>
    )
};

const EditAboModal = ({ item, modal, setModal, updateAbo }) => {
    const { request } = useRequest();
    const closeModal = () => {
        setModal(false);
    }
    const [message, setMessage] = useState("");
    const formRef = useRef();

    const editAbo = async (values) => {
        try {
            let path = location.pathname.split("/");
            if (path.length < 4) return;

            let id = path[3];
            let data = { ...values, AboID: item.AboID, OrganisationID: item.OrganisationID };
            setMessage("");

            const result = await request("/Operator/UpdateAbo", data);
            const json = result.data;

            if (json.r == 1) {
                toast.success("Paket erfolgreich geändert.");
                updateAbo(data);
                closeModal();
            }
            else {
                setMessage(json.message);
            }
        }
        catch (ex) {
            console.error(ex);
            setMessage("Es ist ein Fehler aufgetreten.");
        }
    }

    let today = new Date();
    let firstDay = new Date(today.getFullYear(), today.getMonth(), 1, 23, 59, 59);
    let lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

    useEffect(() => {
        if (!item) return;

        formRef.current.setFieldValue("Name", item.Name);
        formRef.current.setFieldValue("Price", item.Price);
        formRef.current.setFieldValue("Start", item.Start);
        formRef.current.setFieldValue("End", item.End);
        formRef.current.setFieldValue("Contingent", item.Contingent);
        formRef.current.setFieldValue("Radius", item.Radius);
        formRef.current.setFieldValue("User", item.User);
        formRef.current.setFieldValue("Contactperson", item.Contactperson);
    }, [item]);

    return (
        <Modal show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Paket ändern</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert type="error" message={message} />}

                <Formik
                    innerRef={(f) => (formRef.current = f)}
                    validationSchema={aboSchema}
                    onSubmit={(values) => {
                        editAbo(values);
                    }}
                    initialValues={{
                        Start: firstDay.toISOString().split("T")[0],
                        End: lastDay.toISOString().split("T")[0],
                        Contingent: 5,
                        Radius: 25,
                        User: 5,
                        Contactperson: 5
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        touched,
                        isValid,
                        errors,
                    }) => {
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group as={Row} className="mb-3" controlId="Name">
                                    <Form.Label className="required" column sm={6}>Name</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Name && touched.Name} type="text" name="Name" value={values.Name} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Name}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Price">
                                    <Form.Label className="required" column sm={6}>Preis</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Price && touched.Price} type="number" step="0.01" min="0" name="Price" value={values.Price} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Price}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Start">
                                    <Form.Label className="required" column sm={6}>Startdatum</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Start && touched.Start} type="date" name="Start" value={values.Start} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Start}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="End">
                                    <Form.Label className="required" column sm={6}>Enddatum</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.End && touched.End} type="date" name="End" value={values.End} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.End}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Contingent">
                                    <Form.Label className="required" column sm={6}>Kontingent</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Contingent && touched.Contingent} type="number" min="0" name="Contingent" value={values.Contingent} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Contingent}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Radius">
                                    <Form.Label className="required" column sm={6}>Radius</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Radius && touched.Radius} type="number" min="0" name="Radius" value={values.Radius} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Radius}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="User">
                                    <Form.Label className="required" column sm={6}>Anzahl Benutzer</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.User && touched.User} type="number" min="0" name="User" value={values.User} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.User}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="Contactperson">
                                    <Form.Label className="required" column sm={6}>Anzahl Ansprechpartner</Form.Label>
                                    <Col sm={6}>
                                        <Form.Control isInvalid={errors.Contactperson && touched.Contactperson} type="number" min="0" name="Contactperson" value={values.Contactperson} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Contactperson}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <MandatoryFieldNotice />
                            </Form>
                        )
                    }}
                </Formik>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Abbrechen
                </Button>
                <Button variant="primary" type="button" onClick={(e) => formRef.current.handleSubmit()}>
                    Speichern
                </Button>
            </Modal.Footer>
        </Modal>
    )
};

function CouponTable({ coupons }) {
    const foundActiveCoupon = coupons.filter(c => {
        if (c.Activated && c.Expired) {
            const date = new Date();
            const aDate = new Date(c.Activated);
            const eDate = new Date(c.Expired);

            if (date >= aDate && date <= eDate) {
                return true;
            }
        }

        return false;
    });

    return (
        <>
            {foundActiveCoupon.length > 0 && <Alert type="info" hideIcon={true} message={<div>Diese Organisation hat einen Gutschein aktiv</div>} />}
            <div id="coupons">
                <Table>
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Begin</th>
                            <th>Ende</th>
                            <th>Rabatt in %</th>
                            <th>Notiz</th>
                        </tr>
                    </thead>
                    <tbody>
                        {coupons.map((item) => {
                            return <tr className={foundActiveCoupon.find(c => c.CouponID == item.CouponID) ? "bg-success" : ""} key={item.CouponID}>
                                <td><a href={"/Operator/Coupons/" + item.CouponID}>{item.Code}</a></td>
                                <td>{formatDate(item.Activated)}</td>
                                <td>{formatDate(item.Expired)}</td>
                                <td>{item.DiscountPercentage}</td>
                                <td>{item.Notes}</td>
                            </tr>
                        })
                        }
                    </tbody>
                </Table>
            </div>
        </>
    );
}

export default BusinessOperator;